import * as Sentry from '@sentry/react';
import { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Components
import { Button } from '@/components/Button/Button';
import { HiddenIframe } from '@/components/HiddenIframe/HiddenIframe';
import { Iframe } from '@/components/Iframe/Iframe';
import type { PurchaseParams } from '@/components/Payment/Payment.d';
import { PrintAccountNumber } from '@/components/PrintAccountNumber/PrintAccountNumber';
import { ProductAttributes } from '@/components/ProductAttributes/ProductAttributes';
import { Section } from '@/components/Section/Section';
import { Select } from '@/components/Select/Select';
import { Context } from '@/components/Store';
import { type Action, ActionTypes, type Address, type State } from '@/components/Store.d';
import { languageStrings } from '@/l18n/de';
// Modules
import { config, countryList } from '@/lib/config';
import { useMessage } from '@/lib/hooks';
import * as Http from '@/lib/http';
import { error as errorLog, log } from '@/lib/log';
import { getIframeUrl, getIframeUrlWithBundleRef, mapPaymentMethod } from '@/lib/mapping';
import { gatherDeviceData } from '@/lib/mpp';
import {
  useReusePaymentData,
  getCustomParameter,
  getUTMParamsFromSessionStorage,
  hasChallengeRequiredV1,
  hasChallengeRequiredV2,
  hasGatheringDeviceData,
  hasNewCountryCode,
  isSupporter,
  postMessage
} from '@/lib/util';

const Payment = () => {
  const [state, dispatch] = useContext(Context);
  const [paymentMethod, setPaymentMethod] = useState<string>(config.paymentMethods.NOT_SET);
  const [showIframe, setShowIframe] = useState(false);
  const [didSelectPaymentMethod, setDidSelectPaymentMethod] = useState(false);
  const [iframeClass, setIframeClass] = useState('');
  const [isBuyButtonDisabled, setIsBuyButtonDisabled] = useState(true);
  const [postedCountryCode, setPostedCountryCode] = useState('');
  const [hiddenIframeProps, setHiddenIframeProps] = useState({
    value: '',
    actionUrl: '',
    show: false,
    type: ''
  });

  const [bundleRef, setBundleRef] = useState(state?.bundle?.bundleReference ? state.bundle.bundleReference : '');
  const [isFreePurchase, setIsFreePurchase] = useState(!!state?.isFreePurchase);
  const [iframeUrl, setIframeUrl] = useState(getIframeUrlWithBundleRef(bundleRef, isFreePurchase));

  const canReusePaymentData = useReusePaymentData();
  const headingRef = useRef<HTMLHeadingElement>(null);

  const navigate = useNavigate();

  /**
   * Updates payment method in state after successful purchase
   * @param {string | undefined} paymentMethodUsed - The payment method that was used
   * @param {boolean | undefined} shouldSave - Whether the user chose to save this payment method
   */
  function updatePaymentMethodState(paymentMethodUsed?: string, shouldSave?: boolean) {
    if (paymentMethodUsed) {
      dispatch({
        type: ActionTypes.setPaymentMethod,
        payload: paymentMethodUsed
      });
    }

    if (shouldSave && paymentMethodUsed) {
      dispatch({
        type: ActionTypes.setMovableSubscription,
        payload: {
          ...state.upgrade,
          activePaymentMethod: paymentMethodUsed
        }
      });
    }
  }

  useMessage((e: MessageEvent) => onMessageReceive(e, state, dispatch));

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  // TODO: Refactor into reusable hook, consider moving to ProtectedRoute
  useEffect(() => {
    const sessionState = JSON.parse(sessionStorage.getItem('sessionData') || '{}');

    // redirects user to error page, if no session is existing
    if (!(sessionState?.bundle || sessionState.payment?.sessionToken || sessionState?.payment?.accountReference)) {
      log('Change route to', config.routes.error);
      navigate({
        pathname: config.routes.error,
        search: config.search.sessionExpired
      });
    } else {
      dispatch({
        type: ActionTypes.restoreFromStorage,
        payload: sessionState
      });
    }

    dispatch({
      type: ActionTypes.hideError
    });
  }, [dispatch, navigate]);

  useEffect(() => {
    // update isFreePurchase state if necessary
    if (state.isFreePurchase !== isFreePurchase) {
      setIsFreePurchase(state.isFreePurchase);
    }

    // iframe is shown, if the user does not have a valid payment method
    if (!canReusePaymentData && isUpgrade()) {
      log('Payment: Upgrade without valid payment method, user is prompted to insert payment details.');
      setShowIframe(true);
    }

    // update bundleRef if necessary
    if (bundleRef) {
      return;
    } else if (!bundleRef && state?.bundle?.bundleReference) {
      setBundleRef(state.bundle.bundleReference);
    }
    // isUpgrade() uses state.upgrade which is part of state dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, bundleRef, isFreePurchase, setIsFreePurchase, canReusePaymentData]);

  useEffect(() => {
    setIframeUrl(getIframeUrlWithBundleRef(bundleRef, isFreePurchase));
  }, [bundleRef, isFreePurchase]);

  /**
   * Callback function for receiving messages
   * @param {MessageEvent} e
   * @param {State} state
   * @param {React.Dispatch<Action>} dispatch
   */
  function onMessageReceive(e: MessageEvent, state: State, dispatch: React.Dispatch<Action>) {
    if (e.data === 'iFrameError') {
      setIsBuyButtonDisabled(true);
    } else if (e.data === 'iFrameReady') {
      setIsBuyButtonDisabled(false);

      // posts initial country code to iFrame
      if (showIframe && hasNewCountryCode(state, postedCountryCode)) {
        setPostedCountryCode(state.address?.delivery?.country || state.upgrade?.activeCountryCode || '');
        postMessage({
          country: state.address?.delivery?.country || state.upgrade?.activeCountryCode
        });
      } else if (!didSelectPaymentMethod && state.upgrade?.activeCountryCode) {
        postMessage({
          country: state.upgrade?.activeCountryCode
        });

        setDidSelectPaymentMethod(true);
      }
    }

    if (typeof e.data === 'object' && 'reusePaymentMethod' in e.data && typeof e.data.reusePaymentMethod === 'boolean') {
      dispatch({
        type: ActionTypes.setReusePaymentMethod,
        payload: e.data.reusePaymentMethod
      });
    }

    if (e.data === 'formInvalid') {
      setIsBuyButtonDisabled(false);
    }

    log(`Payment: Receiving message from ${e.origin}`, e.data);

    // This message can be sent from any bank, so it shouldn't be validated
    if (e.data && e.data.MD && e.data.PaRes) {
      const searchParams = `${config.search.challengeRequiredV1}&MD=${e.data.MD}&PaRes=${e.data.PaRes}`;
      log('Change route to', config.routes.approve, searchParams);

      navigate({
        pathname: config.routes.approve,
        search: searchParams
      });
    }

    if (e.data && e.data.buySubscription) {
      // TODO: Quickfix durch Messaging ablösen

      const params = { cvv: e.data.cvv || null };
      performPurchase(params);
    }

    if (e.data && e.data.paymentMethod) {
      setPaymentMethod(e.data.paymentMethod);
      dispatch({
        type: ActionTypes.setPaymentMethod,
        payload: e.data.paymentMethod
      });
    }

    if (e.data?.number && e.data?.countryCode) {
      dispatch({
        type: ActionTypes.setPhoneNumber,
        payload: e.data
      });
    }

    if (e.data?.email) {
      dispatch({
        type: ActionTypes.setEmail,
        payload: e.data.email
      });
    }
  }

  /**
   *
   * @param {string} message
   * @returns {boolean}
   */
  function didAlreadyPurchase(message: string) {
    return message && (message.includes('Service Group already purchased') || message.includes('Cannot add another subscription to contractReference'));
  }

  /**
   * Performs purchase process
   * @param {PurchaseParams} params
   */
  function performPurchase(params: PurchaseParams) {
    dispatch({
      type: ActionTypes.disableForm,
      payload: {
        key: 'disableOnSubmit',
        value: true
      }
    });

    setIsBuyButtonDisabled(true);

    const data: PurchaseParams = {
      ...params,
      deliveryInformation: undefined
    };

    saveAddress().then(
      (address) => {
        if (address && typeof address !== 'boolean') {
          data.deliveryInformation = address.deliveryInformationReference;
          data.paymentMethod = params?.paymentMethod ? params.paymentMethod : paymentMethod || '';
        }

        purchase(data).then(
          (result) => {
            // Redirect to Paypal
            if (result?.asynchronousProcessingParameters?.payPalLocation) {
              window.location = result.asynchronousProcessingParameters.payPalLocation;
            }
            // Redirect to Klarna (Sofort)
            else if (result?.asynchronousProcessingParameters?.postRedirectURL) {
              window.location = result.asynchronousProcessingParameters.postRedirectURL;
            }

            // Redirect to Confirmation Page
            else {
              if (hasGatheringDeviceData(result)) {
                // Store payment method and update active payment method if user chose to save it
                updatePaymentMethodState(data.paymentMethod, data.reusePaymentMethod);

                setHiddenIframeProps({
                  show: true,
                  type: 'hidden',
                  actionUrl: result.asynchronousProcessingParameters.threeDSecureAcsUrl,
                  value: result.asynchronousProcessingParameters.threeDSecureEncodedMethodData
                });

                setTimeout(() => {
                  try {
                    setHiddenIframeProps({
                      show: false,
                      type: 'hidden',
                      actionUrl: '',
                      value: ''
                    });

                    log('Change route to', config.routes.approve);
                    navigate(config.routes.approve);
                  } catch (err) {
                    errorLog('Error after timeout while finalizing 3DS flow', err);
                  }
                }, 3000);
              }

              if (hasChallengeRequiredV1(result)) {
                const searchParams = new URLSearchParams({
                  acsUrl: result.asynchronousProcessingParameters.threeDSecureAcsUrl,
                  md: result.asynchronousProcessingParameters.threeDSecureMD,
                  paReq: result.asynchronousProcessingParameters.threeDSecurePaReq
                }).toString();

                setIframeUrl(`${getIframeUrl()}/challenge?${searchParams}`);
                setIframeClass('fullscreen');

                dispatch({
                  type: ActionTypes.enableForm,
                  payload: {
                    key: 'disableOnSubmit',
                    value: false
                  }
                });

                setIsBuyButtonDisabled(false);
              } else if (hasChallengeRequiredV2(result)) {
                log('Change route to', config.routes.approve, config.search.challengeRequiredV2);

                dispatch({
                  type: ActionTypes.setChallengeV2Data,
                  payload: {
                    key: 'challengeRequired',
                    value: {
                      threeDSecureChallengeRequestUrl: result.asynchronousProcessingParameters.threeDSecureChallengeRequestUrl,
                      threeDSecureEncodedCreq: result.asynchronousProcessingParameters.threeDSecureEncodedCreq
                    }
                  }
                });

                navigate({
                  pathname: config.routes.approve,
                  search: config.search.challengeRequiredV2
                });
              } else if (!hasGatheringDeviceData(result) && !hasChallengeRequiredV1(result) && !hasChallengeRequiredV2(result)) {
                updatePaymentMethodState(data.paymentMethod, data.reusePaymentMethod);

                dispatch({
                  type: ActionTypes.purchaseCompleted,
                  payload: result
                });

                log('Change route to', config.routes.confirmation);
                navigate({
                  pathname: config.routes.confirmation
                });
              }
            }
          },
          (error) => {
            errorLog(error);
            const param = error.message && didAlreadyPurchase(error.message) ? '?code=activeProduct' : '';
            log('Change route to', config.routes.error);
            navigate({
              pathname: config.routes.error,
              search: param
            });
          }
        );
      },
      (error) => {
        errorLog(error);
        log('Change route to', config.routes.error);
        navigate(config.routes.error);
      }
    );
  }

  /**
   * Completes Purchase
   * @param {PurchaseParams} params
   * @returns {Promise}
   */
  function purchase(params: PurchaseParams) {
    if (
      state.bundle?.contractSubscriptionGroup.toLowerCase() === config.subscriptionGroups.supporter &&
      state.supporter.selection.period.toLowerCase() === config.supporterPeriods.single
    ) {
      return paymentMethod === config.paymentMethods.KLARNA ? completeSofortPurchase() : completeSinglePurchase(params);
    }
    return completePurchase(params);
  }

  /**
   * Completes Purchase
   * @param {PurchaseParams} params
   * @returns {Promise}
   */
  function completePurchase(params: PurchaseParams) {
    return createCart().then(
      (result) => {
        const data = {
          contractReference: state.bundle?.contractReference,
          cartReference: result?.cartReference ? result?.cartReference : state.bundle?.cartReference,
          subscriptionGroup: state.bundle?.contractSubscriptionGroup,
          countryCode: state?.address?.delivery?.country || state.upgrade.activeCountryCode,
          paymentMethod:
            (canReusePaymentData && params.paymentMethod === state.upgrade.activePaymentMethod ? state.upgrade.activePaymentMethod : params.paymentMethod) ||
            paymentMethod,
          deliveryInformationReference: params.deliveryInformation ? params.deliveryInformation : '',
          voucherCode: state?.voucher?.code ? state.voucher.code : undefined,
          customOrderParameters: {
            Abovorteilsnummer: state.printAccountNumber ? state.printAccountNumber : '',
            ...getUTMParamsFromSessionStorage()
          },
          sessionToken: state.payment.sessionToken,
          cvv: params.cvv || '',
          moveFromSubscriptionReference: state?.upgrade ? state?.upgrade?.moveFromSubscriptionRef : 'movableFailed',
          enhancedProcessingData: {
            ...gatherDeviceData(state.user.ip),
            ip: state.user.ip || ''
          },
          asynchronousInitiationParameters: {
            phoneNumber: state.user.phone?.number,
            phoneNumberCountryCode: state.user.phone?.countryCode,
            emailAddress: state.user?.email
          },
          reusePaymentMethod: params.reusePaymentMethod || state.payment.reusePaymentMethod
        };
        if (state?.bundle?.containsPhysical) {
          const address = {
            Anrede: 'NotKnown',
            Titel: state.address?.delivery.title,
            Vorname: state.address?.delivery.firstName,
            Nachname: state.address?.delivery.lastName,
            PLZ: state.address?.delivery.zip,
            Ort: state.address?.delivery.city,
            Strasse: state.address?.delivery.street,
            HausNr: state.address?.delivery.houseNumber,
            Stiege: state.address?.delivery.stairs,
            Stock: state.address?.delivery.floor,
            Tur: state.address?.delivery.door,
            StreetNo: state.address?.delivery.streetID
          };
          data.customOrderParameters = Object.assign(data.customOrderParameters, address);
        }

        return Http.completePurchase(data, state.user.tokenID).catch((error) => {
          window.postMessage({ error: error.message }, window.location.origin);
        });
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  /**
   * Completes purchase with payment method Klarna (Sofort)
   * @returns {Promise}
   */
  function completeSofortPurchase() {
    const data = {
      sessionId: state.payment.sessionToken,
      description: state?.bundle?.name,
      orderReference: '',
      amount: state?.supporter?.selection?.tier,
      countryCode: state.address?.delivery?.country || state.upgrade?.activeCountryCode
    };

    Sentry.addBreadcrumb({
      category: 'taxCountry',
      message: `taxCountry: ${data.countryCode}, paymentMethod: klarna`,
      level: 'info'
    });

    // TODO: Kann das mittlerweile entfernt werden?
    Sentry.captureMessage('Sofort Payment', 'info');

    return Http.prepareSofort(data, state.user.tokenID).then((result) => {
      const data = {
        sessionId: result.sessionToken,
        asynchronousInitiationParameters: {
          STAGE: 'START',
          COUNTRY_CODE: 'AT',
          ACCOUNT_HOLDER_NAME: 'Account Name'
        }
      };
      return Http.initiateSofort(data, state.user.tokenID);
    });
  }

  /**
   * Completes Single Purchase
   * @param {PurchaseParams} params
   * @returns {Promise}
   */
  function completeSinglePurchase(params: PurchaseParams) {
    const data = {
      paymentMethod:
        (canReusePaymentData && params.paymentMethod === state.upgrade.activePaymentMethod ? state.upgrade.activePaymentMethod : params.paymentMethod) ||
        paymentMethod,
      amount: state?.supporter?.selection?.tier,
      countryCode: state.address?.delivery?.country || state.upgrade?.activeCountryCode,
      cvv: params.cvv || '',
      sessionId: state.payment.sessionToken,
      customOrderParameters: {
        ProductName: state.bundle?.contractSubscriptionGroup,
        CTASolidaritaet: '',
        ...getUTMParamsFromSessionStorage()
      },
      asynchronousInitiationParameters: {
        phoneNumber: state.user.phone?.number,
        phoneNumberCountryCode: state.user.phone?.countryCode,
        emailAddress: state.user?.email
      },
      enhancedProcessingData: {
        ...gatherDeviceData(state.user.ip),
        ip: state.user.ip || ''
      },
      reusePaymentMethod: params.reusePaymentMethod || state.payment.reusePaymentMethod
    };

    return Http.completeSinglePurchase(data, state.user.tokenID);
  }

  /**
   * Creates a cart (Supporter)
   * @returns {Promise}
   */
  function createCart() {
    if (state.bundle?.contractSubscriptionGroup && state.bundle.contractSubscriptionGroup.toLowerCase() === config.subscriptionGroups.supporter) {
      const data = {
        contractReference: state.bundle.contractReference,
        accountReference: state.payment.accountReference,
        description: state.bundle?.name,
        countryCode: state.address?.delivery?.country || state.upgrade?.activeCountryCode,
        grossAmount: state.supporter.selection.tier
      };
      return Http.createCart(data, state.user.tokenID);
    }
    return Promise.resolve(true);
  }

  /**
   * Saves Address
   * @returns {Promise<Address | boolean>}
   */
  function saveAddress(): Promise<Address | boolean> {
    if (state.bundle?.containsPhysical) {
      const data = {
        salutation: 'NotKnown',
        title: state.address?.delivery.title,
        firstname: state.address?.delivery.firstName,
        lastname: state.address?.delivery.lastName,
        stairway: state.address?.delivery.stairs,
        door: state.address?.delivery.door,
        floor: state.address?.delivery.floor,
        number: state.address?.delivery.houseNumber,
        street: state.address?.delivery.street,
        city: state.address?.delivery.city,
        postCode: state.address?.delivery.zip,
        country: 'AUT',
        streetno: state.address?.delivery.streetID
      };
      return Http.setAddress(data, state.user.tokenID);
    }
    return Promise.resolve(true);
  }

  const handleClick = (e: React.FormEvent) => {
    e.preventDefault();

    setIsBuyButtonDisabled(true);

    dispatch({
      type: ActionTypes.hideError
    });

    if (isFreePurchase) {
      performPurchase({ cvv: null, paymentMethod: config.paymentMethods.VOUCHER_PAYMENT });
      return;
    }

    if (state?.upgrade?.message === 'contract already purchased') {
      log('Change route to', config.routes.error);

      navigate({
        pathname: config.routes.error,
        search: config.search.activeProduct
      });
    }

    // Upgrade, not payed with SEPA
    if (!showIframe && canReusePaymentData) {
      performPurchase({ cvv: null, paymentMethod: state.upgrade.activePaymentMethod, reusePaymentMethod: true });
      return;
    }

    const form = document.querySelector('form');

    if (form?.checkValidity()) {
      const payload = {
        address: {
          salutation: 'NotKnown',
          ...state?.address?.delivery,
          email: state?.user?.email
        },
        accountReference: state?.payment?.accountReference,
        clientIP: state?.user?.ip,
        sessionToken: state?.payment?.sessionToken,
        validateInputs: true
      };

      postMessage(payload);
    } else {
      setIsBuyButtonDisabled(false);
    }
  };

  const isUpgrade = () => state.upgrade.isMovableSubscription;

  const hasPrintAccountNumber = () => {
    if (state.bundle?.contractCustomParameters) {
      const onlyForPrint = parseInt(getCustomParameter(state.bundle?.contractCustomParameters, 'OnlyForPrint') || '', 10);
      return typeof onlyForPrint === 'number' && isFinite(onlyForPrint) && onlyForPrint > 0;
    } else return false;
  };

  const onResized = (data: unknown) => {
    log('Resized iFrame:', data);
  };

  const paysWithSepa = () => {
    const paymentMethod = state.upgrade?.activePaymentMethod?.toLowerCase();

    return ['directdebit', 'sepa'].includes(paymentMethod || '');
  };

  const hasNoPaymentMethod = state.upgrade?.activePaymentMethod?.toLowerCase() === 'notavailable';

  const handleCancel = () => {
    const target = state?.entryPage || import.meta.env.VITE_APP_SUBSCRIPTIONS_PAGE_URL;

    window.open(target, '_self');
  };

  /**
   *
   * @returns {boolean}
   */
  function showPrintAccountNumber() {
    switch (true) {
      case state?.upgrade?.requirePrintAccountNumber:
      case hasPrintAccountNumber() && !isSupporter(state.bundle):
        return true;

      default:
        return false;
    }
  }

  return (
    <Section className="section-payment">
      <h1
        ref={headingRef}
        tabIndex={-1}
      >
        {languageStrings.payment.heading}
      </h1>

      {!showIframe && canReusePaymentData ? (
        <>
          <p>{languageStrings.payment.body}</p>
          <p>
            <strong>
              {mapPaymentMethod(state.upgrade.activePaymentMethod || '')}
              <br />
              {state.upgrade.activePaymentAccount}
            </strong>
          </p>
        </>
      ) : null}

      <form onSubmit={handleClick}>
        <fieldset
          className="form--fieldset form--fieldset-frameless"
          disabled={state.disableOnSubmit}
        >
          {showPrintAccountNumber() ? <PrintAccountNumber /> : null}

          {canReusePaymentData ? (
            <Button
              ariaLabel={languageStrings.buttons.cancel}
              className="form--button-weak"
              onClick={() => setShowIframe(!showIframe)}
              type="button"
            >
              {showIframe ? languageStrings.buttons.cancel : languageStrings.buttons.changePaymentMethod}
            </Button>
          ) : null}

          {!canReusePaymentData ? (
            <Select
              defaultValue={state?.address?.delivery?.country || state?.upgrade?.activeCountryCode || ''}
              id="country"
              labelText={languageStrings.forms.countryList.label}
              onChange={(event) => {
                log(`${event.target.id} toggled to ${event.target.value}`);
                dispatch({
                  type: ActionTypes.addressChange,
                  payload: {
                    key: 'address.delivery.country',
                    value: event.target.value
                  }
                });

                // @TODO tempoary solution to pass country to iFrame
                postMessage({
                  country: event.target.value
                });
              }}
              options={countryList}
              required={true}
            />
          ) : null}

          {iframeUrl ? (
            <div
              className="payment-iframe"
              hidden={!(showIframe || ((paysWithSepa() || hasNoPaymentMethod) && canReusePaymentData) || !canReusePaymentData)}
            >
              <Iframe
                className={iframeClass}
                frameBorder="0"
                onResized={onResized}
                src={iframeUrl}
                title={languageStrings.payment.iframe.title}
              />
            </div>
          ) : null}

          <ProductAttributes />

          <div className="actions">
            <Button
              ariaLabel={languageStrings.buttons.cancel}
              className="form--button-weak"
              onClick={handleCancel}
              type="button"
            >
              {languageStrings.buttons.cancel}
            </Button>
            <Button
              ariaLabel={languageStrings.buttons.buy}
              className={['form--button-cta', state.disableOnSubmit ? 'loading' : ''].join(' ')}
              disabled={(isFreePurchase || canReusePaymentData) && !state.disableOnSubmit ? false : isBuyButtonDisabled}
              type="submit"
            >
              {languageStrings.buttons.buy}
            </Button>
          </div>
        </fieldset>
      </form>

      {hiddenIframeProps.show ? (
        <HiddenIframe
          actionUrl={hiddenIframeProps.actionUrl}
          type={hiddenIframeProps.type}
          value={hiddenIframeProps.value}
        />
      ) : null}
    </Section>
  );
};

export { Payment };
